/**
 * KaTeX Content Component
 * Renders mathematical expressions using KaTeX and TikZ diagrams using TikZJax
 * For TikZ diagrams, uses iframe approach for proper rendering
 */
import React, { useState, useEffect, useRef } from 'react';

export const formatInlineMarkup = (value = '') => String(value)
  .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
  .replace(/(^|[^*])\*([^*]+?)\*(?!\*)/g, '$1<em>$2</em>')
  .replace(/__([^_]+?)__/g, '<u>$1</u>')
  .replace(/(^|[^\w])_([^_\n]+?)_(?!\w)/g, '$1<u>$2</u>');

const KaTeXContent = ({ content, className = "" }) => {
  const [hasTikZ, setHasTikZ] = useState(false);
  const iframeRef = useRef(null);
  const contentRef = useRef(null);
  const [isKaTeXLoaded, setIsKaTeXLoaded] = useState(false);
  const [renderedContent, setRenderedContent] = useState(null);
  const lastRenderedContentRef = useRef(null);

  const formattedContent = formatInlineMarkup(content || '');

  // Check if content has TikZ diagrams
  useEffect(() => {
    if (formattedContent) {
      const containsTikZ = formattedContent.includes('type="text/tikz"') || formattedContent.includes("type='text/tikz'");
      setHasTikZ(containsTikZ);
      console.log('KaTeXContent: Content has TikZ diagrams:', containsTikZ);
    }
  }, [formattedContent]);

  useEffect(() => {
    // Check if KaTeX is available
    if (window.katex && window.renderMathInElement) {
      setIsKaTeXLoaded(true);
    } else {
      // Load KaTeX if not available
      const loadKaTeX = async () => {
        try {
          if (!document.querySelector('link[href*="katex"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.css';
            document.head.appendChild(link);
          }
          
          if (!window.katex) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.js';
            script.onload = () => {
              const autoRenderScript = document.createElement('script');
              autoRenderScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/contrib/auto-render.min.js';
              autoRenderScript.onload = () => {
                setIsKaTeXLoaded(true);
              };
              document.head.appendChild(autoRenderScript);
            };
            document.head.appendChild(script);
          }
        } catch (error) {
          console.error('Error loading KaTeX:', error);
        }
      };
      loadKaTeX();
    }
  }, []);

  // For content with TikZ, use iframe approach
  useEffect(() => {
    if (hasTikZ && iframeRef.current && formattedContent) {
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
      
      const fullHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.22/dist/katex.min.css">
          <link rel="stylesheet" type="text/css" href="https://tikzjax.com/v1/fonts.css">
          <style>
            body { 
              margin: 0; 
              padding: 0; 
              font-family: Arial, sans-serif;
              line-height: 1.6;
            }
            /* Answer input styles */
            .answer-input { 
              margin-top: 0.75rem; 
              padding: 0.5rem; 
              background: #f9f9f9; 
              border-radius: 0.25rem; 
            }
            .answer-input input, .answer-input textarea { 
              width: 100% !important;
              padding: 0.5rem;
              border: 1px solid #d1d5db;
              border-radius: 0.25rem;
              font-family: inherit;
              font-size: 1rem;
              margin-bottom: 0.25rem;
            }
            .answer-input input {
              min-height: 2.5rem;
            }
            .answer-input textarea {
              min-height: 6rem;
              resize: vertical;
            }
            .answer-input label { 
              cursor: pointer;
              display: block;
              margin-bottom: 0.25rem;
            }
            /* MCQ options */
            .answer-input input[type="radio"],
            .answer-input input[type="checkbox"] {
              width: auto !important;
              margin-right: 0.5rem;
              min-height: auto;
            }
          </style>
        </head>
        <body>
          ${formattedContent}
          <script src="https://tikzjax.com/v1/tikzjax.js"></script>
        </body>
        </html>
      `;
      
      iframeDoc.open();
      iframeDoc.write(fullHTML);
      iframeDoc.close();
      
      console.log('KaTeXContent: Iframe content loaded with TikZJax');
      
      // Adjust iframe height after content loads
      setTimeout(() => {
        const currentIframe = iframeRef.current;
        const iframeBody = currentIframe?.contentDocument?.body;
        if (currentIframe && iframeBody) {
          const iframeBody = iframeRef.current.contentDocument.body;
          const height = iframeBody.scrollHeight;
          currentIframe.style.height = height + 'px';
          console.log('KaTeXContent: Iframe height adjusted to', height);
        }
      }, 2000); // Wait for TikZ to render
    }
  }, [hasTikZ, formattedContent]);

  // For content without TikZ, use normal rendering
  useEffect(() => {
    if (!hasTikZ && isKaTeXLoaded && contentRef.current && window.renderMathInElement && formattedContent) {
      // Only re-render if content has actually changed
      if (lastRenderedContentRef.current === formattedContent) {
        return;
      }
      
      try {
        // Set the HTML content first
        contentRef.current.innerHTML = formattedContent;
        
        // Then render math
        window.renderMathInElement(contentRef.current, {
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\[', right: '\\]', display: true },
            { left: '\\(', right: '\\)', display: false },
          ],
          throwOnError: false,
          errorColor: '#cc0000',
          strict: false,
        });
        
        // Mark this content as rendered
        lastRenderedContentRef.current = formattedContent;
      } catch (error) {
        console.error('Error rendering KaTeX:', error);
      }
    }
  }, [hasTikZ, isKaTeXLoaded, formattedContent]);

  // Render iframe for TikZ content, regular div for non-TikZ content
  if (hasTikZ) {
    return (
      <iframe
        ref={iframeRef}
        className={className}
        style={{
          width: '100%',
          border: 'none',
          minHeight: '400px',
          overflow: 'hidden'
        }}
        title="Worksheet Content with Diagrams"
      />
    );
  }

  return (
    <div 
      ref={contentRef}
      className={className}
    />
  );
};

export default KaTeXContent;
